import { FC, useContext, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";

import { AppleMap, MapLocation } from "capitalrx-components-2.0/lib/FindBestPriceTool/AppleMap";
import { Dropdown, Form, useForm } from "capitalrx-components-2.0/lib/forms";
import { Button, Buttons, Icon, Link, Message, Messages, Panel, Spinner } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Title } from "capitalrx-components-2.0/lib/typography";

import { FindBestPrice } from "@/api/interfaces";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants/layouts";
import { ThemeSelectorContext } from "@/context";
import { hexToRgbColor } from "@/utils";
import {
  CheckCoverageSearch,
  formatDistance,
  formatDollars,
  isPriceAvailable,
  patientPayAmount,
  pharmacyAddress,
  RESULTS_PER_PAGE,
} from "./utils";

import styles from "./CheckCoverage.module.scss";

export type SortOption = "distance" | "price";

interface CheckCoverageResultsProps {
  search: CheckCoverageSearch;
  quantityLabel: string;
  pricesResponse?: FindBestPrice.PricesListResponse;
  isLoading: boolean;
  error?: string;
  pageNumber: number;
  sortBy: SortOption;
  onSortChange: (sortBy: SortOption) => void;
  onPageChange: (pageNumber: number) => void;
  onSelect: (price: FindBestPrice.Price) => void;
  onBack: () => void;
}

function sortPrices(prices: FindBestPrice.Price[], sortBy: SortOption): FindBestPrice.Price[] {
  const sorted = [...prices];
  if (sortBy === "price") {
    sorted.sort((a, b) => {
      if (isPriceAvailable(a) !== isPriceAvailable(b)) return isPriceAvailable(a) ? -1 : 1;
      return Number(patientPayAmount(a)) - Number(patientPayAmount(b));
    });
  } else {
    sorted.sort((a, b) => (a.pharmacy?.distance ?? 0) - (b.pharmacy?.distance ?? 0));
  }
  return sorted;
}

export const CheckCoverageResults: FC<CheckCoverageResultsProps> = ({
  search,
  quantityLabel,
  pricesResponse,
  isLoading,
  error,
  pageNumber,
  sortBy,
  onSortChange,
  onPageChange,
  onSelect,
  onBack,
}) => {
  const { primary_color } = useContext(ThemeSelectorContext);
  const { sixFull } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);
  const sortForm = useForm<{ sort_by: SortOption }>({ defaultValues: { sort_by: sortBy } });
  const watchedSortBy = useWatch({ control: sortForm.control, name: "sort_by" });

  useEffect(() => {
    if (watchedSortBy && watchedSortBy !== sortBy) {
      onSortChange(watchedSortBy);
    }
  }, [watchedSortBy, sortBy, onSortChange]);

  const prices = useMemo(() => sortPrices(pricesResponse?.prices || [], sortBy), [pricesResponse, sortBy]);
  const resultCount = Number(pricesResponse?.result_count || prices.length);
  const lastPage = Math.max(Math.ceil(resultCount / RESULTS_PER_PAGE), 1);
  const firstResult = resultCount === 0 ? 0 : (pageNumber - 1) * RESULTS_PER_PAGE + 1;
  const lastResult = Math.min(firstResult + prices.length - 1, resultCount);

  const mapLocations = useMemo<MapLocation[]>(
    () => prices
      .filter((price) => price.pharmacy?.latitude && price.pharmacy?.longitude)
      .map((price) => ({
        latitude: parseFloat(price.pharmacy.latitude!),
        longitude: parseFloat(price.pharmacy.longitude!),
        title: price.pharmacy.name,
      })),
    [prices],
  );

  return (
    <Stack spacing="medium-large" className={styles.WideContainer}>
      <Stack spacing="small">
        <Shelf spacing="mini">
          <Icon name="arrow-left" color={hexToRgbColor(primary_color)} />
          <Link
            to="#"
            onClick={(event) => {
              event.preventDefault();
              onBack();
            }}
          >
            Back
          </Link>
          <Shelf.Spacer />
        </Shelf>
        <Title>{search.drug.drug_name}</Title>
        <Body>{search.drug.drug_type}</Body>
        <Shelf spacing="medium">
          <Body size="mini">Dosage: {search.dosage}</Body>
          <Body size="mini">Quantity: {quantityLabel}</Body>
          <Body size="mini">Days Supply: {search.days_supply} Days</Body>
        </Shelf>
      </Stack>

      <Grid>
        <Grid.Span columns={sixFull}>
          <Stack spacing="medium">
            <Body>
              The patient&apos;s responsibility may vary as it is subject to the patient&apos;s benefits and coverage
              at the time of service.
            </Body>

            {(pricesResponse?.custom_member_messages || []).map((message) => (
              <Message key={message} appearance={Messages.Informational} visible>
                {message}
              </Message>
            ))}

            <Shelf spacing="small" className={styles.SortBar}>
              <Form form={sortForm}>
                <Dropdown.Field name="sort_by" label="Sort by" hideLabel required className={styles.SortDropdown}>
                  <Dropdown.Option value="distance">Distance</Dropdown.Option>
                  <Dropdown.Option value="price">Price: Low to High</Dropdown.Option>
                </Dropdown.Field>
              </Form>
              <Shelf.Spacer />
              <Semibold>Estimated amount patient will pay:</Semibold>
            </Shelf>

            {error && (
              <Message appearance={Messages.Error} visible>
                {error}
              </Message>
            )}

            {isLoading ? (
              <Stack alignment="center">
                <Spinner size="large" />
              </Stack>
            ) : (
              <Stack spacing="small">
                {prices.map((price, index) => (
                  <Panel key={price.pharmacy.provider_id} testId={`check_coverage_result_${index}`}>
                    <Shelf spacing="small">
                      <Stack spacing="none">
                        <Semibold>{`${firstResult + index}. ${price.pharmacy.name}`}</Semibold>
                        {formatDistance(price.pharmacy.distance) && (
                          <Body size="mini">{formatDistance(price.pharmacy.distance)}</Body>
                        )}
                        <Body size="mini">{pharmacyAddress(price.pharmacy)}</Body>
                      </Stack>
                      <Shelf.Spacer />
                      {isPriceAvailable(price) ? (
                        <Button
                          appearance={Buttons.Functional.Secondary.Small}
                          icon="arrow-right"
                          iconPosition="right"
                          onClick={() => onSelect(price)}
                        >
                          {formatDollars(patientPayAmount(price))}
                        </Button>
                      ) : (
                        <Body size="mini">Pricing or fulfillment unavailable.</Body>
                      )}
                    </Shelf>
                  </Panel>
                ))}

                {prices.length === 0 && !error && (
                  <Message appearance={Messages.Warning} visible>
                    No pharmacies were found for this search.
                  </Message>
                )}
              </Stack>
            )}

            {resultCount > 0 && (
              <Shelf spacing="mini">
                <Body size="mini">{`Showing ${firstResult}-${lastResult} of ${resultCount} results`}</Body>
                <Shelf.Spacer />
                <Button
                  appearance={Buttons.Functional.Secondary.Small}
                  icon="chevron-start"
                  disabled={isLoading || pageNumber <= 1}
                  onClick={() => onPageChange(1)}
                />
                <Button
                  appearance={Buttons.Functional.Secondary.Small}
                  icon="chevron-left"
                  disabled={isLoading || pageNumber <= 1}
                  onClick={() => onPageChange(pageNumber - 1)}
                />
                <Button
                  appearance={Buttons.Functional.Secondary.Small}
                  icon="chevron-right"
                  disabled={isLoading || pageNumber >= lastPage}
                  onClick={() => onPageChange(pageNumber + 1)}
                />
                <Button
                  appearance={Buttons.Functional.Secondary.Small}
                  icon="chevron-end"
                  disabled={isLoading || pageNumber >= lastPage}
                  onClick={() => onPageChange(lastPage)}
                />
              </Shelf>
            )}
          </Stack>
        </Grid.Span>
        <Grid.Span columns={sixFull}>
          <AppleMap
            className={styles.ResultsMap}
            isLoading={isLoading}
            locations={mapLocations}
            center={{
              latitude: parseFloat(search.location.latitude),
              longitude: parseFloat(search.location.longitude),
            }}
          />
        </Grid.Span>
      </Grid>
    </Stack>
  );
};
